import { type GraphQLTaggedNode } from 'react-relay';
import {
	getRequest,
	type NormalizationLinkedField,
	type NormalizationSelection,
} from 'relay-runtime';

type ResultRecord = Record<string, unknown>;
type TransformResult =
	| { value: unknown; changed: boolean; omitted?: false }
	| { changed: true; omitted: true };

function isResultRecord(value: unknown): value is ResultRecord {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getResultKey(selection: NormalizationLinkedField): string {
	return selection.alias ?? selection.name;
}

function hasDirectTypenameSelection(selections: readonly NormalizationSelection[]): boolean {
	return selections.some(
		(selection) => selection.kind === 'ScalarField' && selection.name === '__typename',
	);
}

function hasResultDataForSelections(
	data: ResultRecord,
	selections: readonly NormalizationSelection[],
): boolean {
	let hasExpectedSelectionData = false;
	const selectionsToCheck = [...selections];

	// Typename inference is intentionally conservative: one shared field is
	// not enough to prove which concrete type produced an abstract payload.
	while (selectionsToCheck.length > 0) {
		const selection = selectionsToCheck.pop();
		if (!selection) {
			break;
		}

		switch (selection.kind) {
			case 'ScalarField': {
				if (selection.name === '__typename') {
					break;
				}

				hasExpectedSelectionData = true;
				if (!(selection.name in data)) {
					return false;
				}
				break;
			}
			case 'LinkedField': {
				hasExpectedSelectionData = true;
				if (!(getResultKey(selection) in data)) {
					return false;
				}
				break;
			}
			case 'ActorChange': {
				hasExpectedSelectionData = true;
				if (!(getResultKey(selection.linkedField) in data)) {
					return false;
				}
				break;
			}
			case 'ClientExtension':
			case 'Condition':
			case 'Defer':
			case 'InlineFragment':
			case 'Stream': {
				selectionsToCheck.push(...selection.selections);
				break;
			}
			case 'ClientComponent': {
				if (!('selections' in selection.fragment)) {
					break;
				}

				selectionsToCheck.push(...selection.fragment.selections);
				break;
			}
			default:
				break;
		}
	}

	return hasExpectedSelectionData;
}

function collectMatchingInlineFragmentTypes(
	data: ResultRecord,
	selections: readonly NormalizationSelection[],
	matchingTypes: Set<string>,
): void {
	selections.forEach((selection) => {
		switch (selection.kind) {
			case 'InlineFragment':
				if (hasResultDataForSelections(data, selection.selections)) {
					matchingTypes.add(selection.type);
				}
				return;
			case 'ClientExtension':
			case 'Condition':
			case 'Defer':
			case 'Stream':
				collectMatchingInlineFragmentTypes(data, selection.selections, matchingTypes);
				return;
			case 'ClientComponent':
				if ('selections' in selection.fragment) {
					collectMatchingInlineFragmentTypes(data, selection.fragment.selections, matchingTypes);
				}
				return;
			default:
				return;
		}
	});
}

function inferInlineFragmentTypename(
	data: ResultRecord,
	selections: readonly NormalizationSelection[],
): string | null {
	const matchingTypes = new Set<string>();
	collectMatchingInlineFragmentTypes(data, selections, matchingTypes);

	// Apollo's fragment matcher needs a concrete __typename for inline fragments.
	// Relay reader data can omit it, so infer only when the result shape points to
	// exactly one fragment type. Ambiguous abstract payloads are left unchanged.
	return matchingTypes.size === 1 ? [...matchingTypes][0] : null;
}

function addLinkedFieldTypenames(
	value: unknown,
	selection: NormalizationLinkedField,
): TransformResult {
	if (value == null) {
		return { value, changed: false };
	}

	if (selection.plural) {
		if (!Array.isArray(value)) {
			return { value, changed: false };
		}

		let nextItems: unknown[] | null = null;
		let omittedItem = false;

		value.forEach((item, index) => {
			if (omittedItem) return;

			const result = addLinkedFieldTypenames(item, {
				...selection,
				plural: false,
			});

			if (result.omitted) {
				// Keep plural field semantics intact. Filtering a single unsafe item
				// would shift list indexes/order, so omit the whole mirrored field.
				omittedItem = true;
				return;
			}

			if (result.changed) {
				nextItems ??= [...value];
				nextItems[index] = result.value;
			}
		});

		if (omittedItem) {
			return { changed: true, omitted: true };
		}

		return nextItems ? { value: nextItems, changed: true } : { value, changed: false };
	}

	if (!isResultRecord(value)) {
		return { value, changed: false };
	}

	let nextValue = value;
	let changed = false;

	if (selection.concreteType && nextValue.__typename == null) {
		nextValue = {
			...nextValue,
			__typename: selection.concreteType,
		};
		changed = true;
	}

	const nestedResult = addTypenamesForSelections(nextValue, selection.selections);
	if (nestedResult.changed) {
		nextValue = nestedResult.value;
		changed = true;
	}

	if (
		!selection.concreteType &&
		nextValue.__typename == null &&
		hasDirectTypenameSelection(selection.selections)
	) {
		// Apollo 1.x rejects writes that replace a generated linked record with a
		// typename-less payload. Relay query data can omit __typename for abstract
		// fields, so omit only that untypable child and keep the rest of the mirror.
		return { changed: true, omitted: true };
	}

	return { value: nextValue, changed };
}

function addTypenamesForSelections(
	data: ResultRecord,
	selections: readonly NormalizationSelection[],
): { value: ResultRecord; changed: boolean } {
	let nextData = data;
	let changed = false;

	if (nextData.__typename == null) {
		const inferredTypename = inferInlineFragmentTypename(nextData, selections);
		if (inferredTypename) {
			nextData = {
				...nextData,
				__typename: inferredTypename,
			};
			changed = true;
		}
	}

	selections.forEach((selection) => {
		switch (selection.kind) {
			case 'LinkedField': {
				const key = getResultKey(selection);
				if (!(key in nextData)) return;

				const result = addLinkedFieldTypenames(nextData[key], selection);
				if (!result.changed) return;

				if (!changed) {
					nextData = { ...nextData };
					changed = true;
				}
				if (result.omitted) {
					delete nextData[key];
				} else {
					nextData[key] = result.value;
				}
				return;
			}
			case 'ActorChange': {
				const key = getResultKey(selection.linkedField);
				if (!(key in nextData)) return;

				const result = addLinkedFieldTypenames(nextData[key], selection.linkedField);
				if (!result.changed) return;

				if (!changed) {
					nextData = { ...nextData };
					changed = true;
				}
				if (result.omitted) {
					delete nextData[key];
				} else {
					nextData[key] = result.value;
				}
				return;
			}
			case 'ClientExtension':
			case 'Condition':
			case 'Defer':
			case 'InlineFragment':
			case 'Stream': {
				const result = addTypenamesForSelections(nextData, selection.selections);
				if (!result.changed) return;

				nextData = result.value;
				changed = true;
				return;
			}
			case 'ClientComponent': {
				if (!('selections' in selection.fragment)) return;

				const result = addTypenamesForSelections(nextData, selection.fragment.selections);
				if (!result.changed) return;

				nextData = result.value;
				changed = true;
				return;
			}
			default:
				return;
		}
	});

	return { value: nextData, changed };
}

export function addRelayConcreteTypenamesToQueryData<TData>(
	query: GraphQLTaggedNode,
	data: TData,
): TData {
	if (!isResultRecord(data)) {
		return data;
	}

	const request = getRequest(query);
	const result = addTypenamesForSelections(data, request.operation.selections);

	return result.value as TData;
}
